import { Pane } from 'tweakpane'
import * as EssentialsPlugin from '@tweakpane/plugin-essentials'
import { defaultParams, FOLDERS_KEY } from '../config/defaults.js'
import { params, saveParams, loadFolderState, saveFolderState, storageKey } from '../config/persistence.js'
import { builtinPresets, userPresets, saveUserPresets } from '../config/presets.js'
import { debugPresets } from '../config/debugPresets.js'

const isDebug = new URLSearchParams(window.location.search).has('debug')
import { colorFromHex } from '../rendering/particles.js'
import { viewport } from '../core/viewport.js'

export const initControls = ({ particleSystem, compute, controls, renderer, bloomPass, trailFeedbackMaterial, trailOutputMaterial, trailRenderTargetA, trailRenderTargetB }) => {
    const pane = new Pane({ title: 'Controlli' })
    pane.registerPlugin(EssentialsPlugin)

    const savedFolders = loadFolderState()
    const folderExpanded = (key, fallback = true) => savedFolders[key] !== undefined ? savedFolders[key] : fallback

    const applyParams = () => {
        renderer.setClearColor(params.backgroundColor)
        particleSystem.material.uniforms.uSize.value = params.particleSize
        particleSystem.material.uniforms.uColor.value = colorFromHex(params.particleColor)
        particleSystem.material.uniforms.uColorShift.value = colorFromHex(params.colorShift)
        particleSystem.material.uniforms.uColorVariance.value = params.colorVariance
        particleSystem.material.uniforms.uOpacityVariance.value = params.opacityVariance
        particleSystem.material.uniforms.uSizeVariance.value = params.sizeVariance
        particleSystem.material.uniforms.uFlashIntensity.value = params.flashIntensity
        particleSystem.material.uniforms.uDimIntensity.value = params.dimIntensity
        particleSystem.material.uniforms.uSaturation.value = params.saturation !== undefined ? params.saturation : 1.0
        particleSystem.material.uniforms.uParticleShape.value = params.particleShape
        particleSystem.material.uniforms.uShapeSoftness.value = params.shapeSoftness
        particleSystem.material.uniforms.uDofFocus.value = params.dofEnabled ? params.dofFocus : 0.0
        particleSystem.material.uniforms.uDofAperture.value = params.dofEnabled ? params.dofAperture : 0.0
        particleSystem.material.uniforms.uDofMaxBlur.value = params.dofEnabled ? params.dofMaxBlur : 0.0
        const revealR = params.revealEnabled ? params.revealRadius : 0.0
        particleSystem.material.uniforms.uRevealRadius.value = revealR
        particleSystem.material.uniforms.uRevealSoftness.value = params.revealSoftness
        if (window._splatMaterial) {
            window._splatMaterial.uniforms.uRevealRadius.value = revealR
            window._splatMaterial.uniforms.uRevealSoftness.value = params.revealSoftness
        }
        if (window._splatCloud) window._splatCloud.visible = params.revealEnabled
        compute.velocityVariable.material.uniforms.uFlowFieldInfluence.value = params.flowInfluence
        compute.velocityVariable.material.uniforms.uFlowFieldStrength.value = params.flowStrength
        compute.velocityVariable.material.uniforms.uFlowFieldFrequency.value = params.flowFrequency
        compute.particlesVariable.material.uniforms.uMouseRadius.value = params.mouseRadius
        compute.velocityVariable.material.uniforms.uMouseRadius.value = params.mouseRadius
        compute.particlesVariable.material.uniforms.uMouseAspect.value = params.mouseAspect
        compute.velocityVariable.material.uniforms.uMouseAspect.value = params.mouseAspect
        compute.velocityVariable.material.uniforms.uSuckStrength.value = params.suckStrength
        compute.velocityVariable.material.uniforms.uWindStrength.value = params.windStrength
        compute.particlesVariable.material.uniforms.uMouseTrail.value = params.mouseTrail
        compute.particlesVariable.material.uniforms.uDecayRate.value = params.respawnRate
        compute.velocityVariable.material.uniforms.uDamping.value = params.damping
        compute.velocityVariable.material.uniforms.uTurbulenceStrength.value = params.turbulenceStrength
        compute.velocityVariable.material.uniforms.uTurbulenceFrequency.value = params.turbulenceFrequency
        compute.velocityVariable.material.uniforms.uMorphStrength.value = params.morphEnabled ? params.morphStrength : 0
        controls.enableRotate = params.enableRotate
        controls.enableZoom = params.enableZoom
        controls.enablePan = params.enablePan
        controls.autoRotate = params.autoRotate
        controls.autoRotateSpeed = params.autoRotateSpeed
        bloomPass.strength = params.bloomStrength
        bloomPass.radius = params.bloomRadius
        bloomPass.threshold = params.bloomThreshold
        renderer.toneMapping = params.toneMapping
        renderer.toneMappingExposure = params.exposure
        trailFeedbackMaterial.uniforms.trailAmount.value = params.trailAmount / 100
        trailFeedbackMaterial.uniforms.blurAmount.value = params.trailBlurAmount
        trailFeedbackMaterial.uniforms.brightPersist.value = params.trailBrightPersist
        trailFeedbackMaterial.uniforms.blendMode.value = params.trailBlendMode
        trailOutputMaterial.uniforms.trailAmount.value = params.trailAmount / 100
        trailOutputMaterial.uniforms.brightPersist.value = params.trailBrightPersist
        trailOutputMaterial.uniforms.blendMode.value = params.trailBlendMode
        if (window._suctionBars) {
            window._suctionBars.updateUniforms()
            window._suctionBars.syncMeshesFromParams()
        }
        const tw = Math.floor(viewport.width * viewport.pixelRatio * params.trailResolutionScale)
        const th = Math.floor(viewport.height * viewport.pixelRatio * params.trailResolutionScale)
        trailRenderTargetA.setSize(tw, th)
        trailRenderTargetB.setSize(tw, th)
        trailFeedbackMaterial.uniforms.texelSize.value.set(1.0 / tw, 1.0 / th)
        pane.refresh()
    }

    // --- Preset ---
    const presetFolder = pane.addFolder({ title: 'Preset', expanded: folderExpanded('presetFolder') })

    const rebuildPresetButtons = () => {
        const children = [...presetFolder.children]
        children.forEach(c => presetFolder.remove(c))

        for (const [name, preset] of Object.entries(builtinPresets)) {
            presetFolder.addButton({ title: name }).on('click', () => {
                const needsReload = (preset.particleCount && preset.particleCount !== params.particleCount)
                    || (preset.shape && preset.shape !== params.shape)
                    || (preset.shapeText && preset.shapeText !== params.shapeText)
                    || (preset.shapeScale && preset.shapeScale !== params.shapeScale)
                    || (preset.morphTarget && preset.morphTarget !== params.morphTarget)
                    || (preset.plyFile && preset.plyFile !== params.plyFile)
                Object.assign(params, JSON.parse(JSON.stringify({ ...defaultParams, ...preset })))
                saveParams()
                if (needsReload) { location.reload(); return }
                applyParams()
            })
        }

        if (isDebug && Object.keys(debugPresets).length > 0) {
            presetFolder.addBlade({ view: 'separator' })
            for (const [name, preset] of Object.entries(debugPresets)) {
                presetFolder.addButton({ title: `🧪 ${name}` }).on('click', () => {
                    const needsReload = (preset.particleCount && preset.particleCount !== params.particleCount)
                        || (preset.shape && preset.shape !== params.shape)
                        || (preset.shapeText && preset.shapeText !== params.shapeText)
                        || (preset.shapeScale && preset.shapeScale !== params.shapeScale)
                        || (preset.morphTarget && preset.morphTarget !== params.morphTarget)
                        || (preset.plyFile && preset.plyFile !== params.plyFile)
                    Object.assign(params, JSON.parse(JSON.stringify({ ...defaultParams, ...preset })))
                    saveParams()
                    if (needsReload) { location.reload(); return }
                    applyParams()
                })
            }
        }

        presetFolder.addBlade({ view: 'separator' })

        for (const [name, preset] of Object.entries(userPresets)) {
            const btn = presetFolder.addButton({ title: `★ ${name}` }).on('click', () => {
                const needsReload = (preset.particleCount && preset.particleCount !== params.particleCount)
                    || (preset.shape && preset.shape !== params.shape)
                    || (preset.shapeText && preset.shapeText !== params.shapeText)
                    || (preset.shapeScale && preset.shapeScale !== params.shapeScale)
                    || (preset.morphTarget && preset.morphTarget !== params.morphTarget)
                    || (preset.plyFile && preset.plyFile !== params.plyFile)
                Object.assign(params, JSON.parse(JSON.stringify({ ...defaultParams, ...preset })))
                saveParams()
                if (needsReload) { location.reload(); return }
                applyParams()
            })
            btn.element.addEventListener('contextmenu', (e) => {
                e.preventDefault()
                if (!confirm(`Eliminare il preset "${name}"?`)) return
                delete userPresets[name]
                saveUserPresets()
                rebuildPresetButtons()
            })
        }

        presetFolder.addBlade({ view: 'separator' })
        presetFolder.addButton({ title: '+ Salva preset corrente' }).on('click', () => {
            const name = prompt('Nome del preset:')
            if (!name || !name.trim()) return
            const trimmed = name.trim()
            if (builtinPresets[trimmed]) {
                alert('Non puoi sovrascrivere un preset predefinito.')
                return
            }
            if (userPresets[trimmed] && !confirm(`Il preset "${trimmed}" esiste già. Sovrascrivere?`)) return
            userPresets[trimmed] = { ...params }
            saveUserPresets()
            rebuildPresetButtons()
        })
    }

    rebuildPresetButtons()

    // --- Particelle ---
    const particlesFolder = pane.addFolder({ title: 'Particelle', expanded: folderExpanded('particlesFolder') })
    particlesFolder.addBinding(params, 'shape', {
        label: 'Forma', options: {
            'Testo': 'text', 'Sfera': 'sphere', 'Torus Knot': 'torusknot',
            'Toro': 'torus', 'Cuore': 'heart', 'Spirale DNA': 'spiral',
            'Icosaedro': 'icosahedron', 'Ottaedro': 'octahedron',
            'PLY (3D Splat)': 'ply', 'Sparse (viewport)': 'field',
        }
    }).on('change', () => { saveParams(); location.reload() })
    particlesFolder.addBinding(params, 'shapeText', { label: 'Testo' })
        .on('change', () => { if (params.shape === 'text') { saveParams(); location.reload() } })
    particlesFolder.addBinding(params, 'shapeScale', { label: 'Scala forma', min: 0.3, max: 3, step: 0.1 })
        .on('change', () => { saveParams(); location.reload() })
    particlesFolder.addBinding(params, 'particleCount', { label: 'Numero particelle', min: 1000, max: 1000000, step: 1000 })
        .on('change', (e) => {
            params.particleCount = e.value
            saveParams()
            location.reload()
        })
    particlesFolder.addBinding(params, 'respawnRate', { label: 'Velocità respawn', min: 0.01, max: 2, step: 0.01 })
        .on('change', (e) => { compute.particlesVariable.material.uniforms.uDecayRate.value = e.value })
    particlesFolder.addBinding(params, 'spawnEdgeBias', { label: 'Spawn ai bordi a piena espansione (1=uniforme)', min: 0.1, max: 1, step: 0.01 })
        .on('change', (e) => { compute.particlesVariable.material.uniforms.uSpawnEdgeBias.value = e.value })
    particlesFolder.addBinding(params, 'spawnEdgeFloor', { label: 'Minimo particelle al centro', min: 0, max: 0.6, step: 0.01 })
        .on('change', (e) => { compute.particlesVariable.material.uniforms.uSpawnEdgeFloor.value = e.value })

    // --- Morph ---
    const morphFolder = pane.addFolder({ title: 'Morph', expanded: folderExpanded('morphFolder') })
    morphFolder.addBinding(params, 'morphEnabled', { label: 'Attivo' })
    morphFolder.addBinding(params, 'morphTarget', {
        label: 'Target', options: {
            'Sfera': 'sphere', 'Torus Knot': 'torusknot', 'Toro': 'torus',
            'Cuore': 'heart', 'Spirale DNA': 'spiral', 'Icosaedro': 'icosahedron',
            'Ottaedro': 'octahedron', 'Testo': 'text',
        }
    }).on('change', () => { saveParams(); location.reload() })
    morphFolder.addBinding(params, 'morphSpeed', { label: 'Velocità', min: 0.05, max: 3, step: 0.05 })
    morphFolder.addBinding(params, 'morphStrength', { label: 'Forza attrazione', min: 0.5, max: 50, step: 0.5 })

    // --- Aspetto ---
    const aspectFolder = pane.addFolder({ title: 'Aspetto', expanded: folderExpanded('aspectFolder') })
    aspectFolder.addBinding(params, 'particleShape', {
        label: 'Forma',
        options: { 'Cerchio': 0, 'Quadrato': 1, 'Diamante': 2, 'Stella': 3, 'Anello': 4 }
    }).on('change', (e) => { particleSystem.material.uniforms.uParticleShape.value = e.value })
    aspectFolder.addBinding(params, 'shapeSoftness', { label: 'Morbidezza bordo', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { particleSystem.material.uniforms.uShapeSoftness.value = e.value })
    aspectFolder.addBinding(params, 'particleSize', { label: 'Dimensione', min: 0, max: 2, step: 0.001 })
        .on('change', (e) => { particleSystem.material.uniforms.uSize.value = e.value })
    aspectFolder.addBinding(params, 'particleColor', { label: 'Colore particelle', view: 'color' })
        .on('change', (e) => { particleSystem.material.uniforms.uColor.value = colorFromHex(e.value) })
    aspectFolder.addBinding(params, 'colorShift', { label: 'Colore secondario', view: 'color' })
        .on('change', (e) => { particleSystem.material.uniforms.uColorShift.value = colorFromHex(e.value) })
    aspectFolder.addBinding(params, 'colorVariance', { label: 'Varianza colore', min: 0, max: 5, step: 0.01 })
        .on('change', (e) => { particleSystem.material.uniforms.uColorVariance.value = e.value })
    aspectFolder.addBinding(params, 'opacityVariance', { label: 'Varianza opacità', min: 0, max: 5, step: 0.01 })
        .on('change', (e) => { particleSystem.material.uniforms.uOpacityVariance.value = e.value })
    aspectFolder.addBinding(params, 'sizeVariance', { label: 'Varianza dimensione', min: 0, max: 5, step: 0.01 })
        .on('change', (e) => { particleSystem.material.uniforms.uSizeVariance.value = e.value })
    aspectFolder.addBinding(params, 'saturation', { label: 'Saturazione', min: 0, max: 3, step: 0.01 })
        .on('change', (e) => { particleSystem.material.uniforms.uSaturation.value = e.value })
    aspectFolder.addBinding(params, 'flashIntensity', { label: 'Flash luce', min: 0, max: 30, step: 0.1 })
        .on('change', (e) => { particleSystem.material.uniforms.uFlashIntensity.value = e.value })
    aspectFolder.addBinding(params, 'dimIntensity', { label: 'Ombra casuale', min: 0, max: 10, step: 0.01 })
        .on('change', (e) => { particleSystem.material.uniforms.uDimIntensity.value = e.value })
    aspectFolder.addBinding(params, 'backgroundColor', { label: 'Colore sfondo', view: 'color' })
        .on('change', (e) => { renderer.setClearColor(e.value) })

    // --- Campo di flusso ---
    const flowFolder = pane.addFolder({ title: 'Campo di flusso', expanded: folderExpanded('flowFolder') })
    flowFolder.addBinding(params, 'flowInfluence', { label: 'Influenza', min: 0, max: 1, step: 0.001 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uFlowFieldInfluence.value = e.value })
    flowFolder.addBinding(params, 'flowStrength', { label: 'Intensità', min: 0, max: 30, step: 0.001 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uFlowFieldStrength.value = e.value })
    flowFolder.addBinding(params, 'flowFrequency', { label: 'Frequenza', min: 0, max: 5, step: 0.001 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uFlowFieldFrequency.value = e.value })

    // --- Mouse ---
    const mouseFolder = pane.addFolder({ title: 'Interazione mouse', expanded: folderExpanded('mouseFolder') })
    mouseFolder.addBinding(params, 'mouseRadius', { label: 'Raggio', min: 0, max: 50, step: 0.1 })
        .on('change', (e) => {
            compute.particlesVariable.material.uniforms.uMouseRadius.value = e.value
            compute.velocityVariable.material.uniforms.uMouseRadius.value = e.value
        })
    mouseFolder.addBinding(params, 'mouseAspect', { label: 'Larghezza barra', min: 0.1, max: 20, step: 0.1 })
        .on('change', (e) => {
            compute.particlesVariable.material.uniforms.uMouseAspect.value = e.value
            compute.velocityVariable.material.uniforms.uMouseAspect.value = e.value
        })
    mouseFolder.addBinding(params, 'alwaysSuck', { label: 'Risucchio sempre attivo' })
    mouseFolder.addBinding(params, 'suckStrength', { label: 'Forza risucchio', min: 0, max: 100, step: 0.1 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uSuckStrength.value = e.value })
    mouseFolder.addBinding(params, 'windStrength', { label: 'Forza vento', min: 0, max: 50, step: 0.1 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uWindStrength.value = e.value })
    mouseFolder.addBinding(params, 'mouseTrail', { label: 'Scia', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { compute.particlesVariable.material.uniforms.uMouseTrail.value = e.value })
    mouseFolder.addBinding(params, 'revealEnabled', { label: 'Rivela con mouse' })
        .on('change', (e) => {
            const r = e.value ? params.revealRadius : 0.0
            particleSystem.material.uniforms.uRevealRadius.value = r
            if (window._splatMaterial) window._splatMaterial.uniforms.uRevealRadius.value = r
            if (window._splatCloud) window._splatCloud.visible = e.value
        })
    mouseFolder.addBinding(params, 'revealRadius', { label: 'Raggio rivelazione', min: 0.5, max: 20, step: 0.1 })
        .on('change', (e) => {
            if (params.revealEnabled) {
                particleSystem.material.uniforms.uRevealRadius.value = e.value
                if (window._splatMaterial) window._splatMaterial.uniforms.uRevealRadius.value = e.value
            }
        })
    mouseFolder.addBinding(params, 'revealSoftness', { label: 'Morbidezza rivelazione', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => {
            particleSystem.material.uniforms.uRevealSoftness.value = e.value
            if (window._splatMaterial) window._splatMaterial.uniforms.uRevealSoftness.value = e.value
        })

    // --- Barre di aspirazione ---
    const syncBars = () => {
        if (window._suctionBars) {
            window._suctionBars.updateUniforms()
            window._suctionBars.syncMeshesFromParams()
        }
    }
    const suctionFolder = pane.addFolder({ title: 'Barre di aspirazione', expanded: folderExpanded('suctionFolder') })
    suctionFolder.addBinding(params, 'suctionBarsEnabled', { label: 'Attive (Spazio)' })
    suctionFolder.addBinding(params, 'suctionBarStrength', { label: 'Forza', min: 0, max: 500, step: 0.5 })
        .on('change', syncBars)
    suctionFolder.addBinding(params, 'suctionBarRadius', { label: 'Raggio influenza', min: 0.2, max: 30, step: 0.1 })
        .on('change', syncBars)
    suctionFolder.addBinding(params, 'suctionFadeSpeed', { label: 'Velocità fade', min: 0.2, max: 10, step: 0.1 })

    suctionFolder.addBlade({ view: 'separator' })
    suctionFolder.addBinding(params, 'suctionBar0Pos', { label: 'Bar 0 — Posizione', x: { step: 0.1 }, y: { step: 0.1 }, z: { step: 0.1 } })
        .on('change', syncBars)
    suctionFolder.addBinding(params, 'suctionBar0Size', { label: 'Bar 0 — Dimensione', x: { min: 0.05, step: 0.05 }, y: { min: 0.05, step: 0.05 }, z: { min: 0.05, step: 0.05 } })
        .on('change', syncBars)

    suctionFolder.addBlade({ view: 'separator' })
    suctionFolder.addBinding(params, 'suctionBar1Pos', { label: 'Bar 1 — Posizione', x: { step: 0.1 }, y: { step: 0.1 }, z: { step: 0.1 } })
        .on('change', syncBars)
    suctionFolder.addBinding(params, 'suctionBar1Size', { label: 'Bar 1 — Dimensione', x: { min: 0.05, step: 0.05 }, y: { min: 0.05, step: 0.05 }, z: { min: 0.05, step: 0.05 } })
        .on('change', syncBars)

    suctionFolder.addBlade({ view: 'separator' })
    suctionFolder.addButton({ title: 'Copia coordinate (JSON)' }).on('click', async () => {
        const coords = {
            suctionBar0Pos: { ...params.suctionBar0Pos },
            suctionBar0Size: { ...params.suctionBar0Size },
            suctionBar1Pos: { ...params.suctionBar1Pos },
            suctionBar1Size: { ...params.suctionBar1Size },
        }
        const text = JSON.stringify(coords, null, 2)
        try {
            await navigator.clipboard.writeText(text)
            console.log('Coordinate copiate negli appunti:\n' + text)
        } catch(err) {
            console.error('Clipboard non disponibile, ecco le coordinate:\n' + text)
        }
    })
    suctionFolder.addButton({ title: 'Reset posizioni di default' }).on('click', () => {
        params.suctionBar0Pos = { ...defaultParams.suctionBar0Pos }
        params.suctionBar0Size = { ...defaultParams.suctionBar0Size }
        params.suctionBar1Pos = { ...defaultParams.suctionBar1Pos }
        params.suctionBar1Size = { ...defaultParams.suctionBar1Size }
        saveParams()
        syncBars()
        pane.refresh()
    })

    // --- Fisica ---
    const physicsFolder = pane.addFolder({ title: 'Fisica', expanded: folderExpanded('physicsFolder') })
    physicsFolder.addBinding(params, 'damping', { label: 'Attrito (meno = più inerzia)', min: 0, max: 30, step: 0.1 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uDamping.value = e.value })
    physicsFolder.addBinding(params, 'turbulenceStrength', { label: 'Turbolenza', min: 0, max: 30, step: 0.1 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uTurbulenceStrength.value = e.value })
    physicsFolder.addBinding(params, 'turbulenceFrequency', { label: 'Frequenza turbolenza', min: 0, max: 20, step: 0.1 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uTurbulenceFrequency.value = e.value })

    // --- Gradiente forza spaziale (flow + turbolenza modulati per posizione) ---
    const gradFolder = pane.addFolder({ title: 'Gradiente forza', expanded: folderExpanded('gradFolder') })
    gradFolder.addBinding(params, 'forceGradEnabled', { label: 'Attivo' })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uForceGradEnabled.value = e.value ? 1.0 : 0.0 })
    gradFolder.addBinding(params, 'forceGradFloor', { label: 'Forza in alto-centro', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uForceGradFloor.value = e.value })
    gradFolder.addBinding(params, 'forceGradPower', { label: 'Curva (contrasto)', min: 0.2, max: 5, step: 0.05 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uForceGradPower.value = e.value })
    gradFolder.addBinding(params, 'forceGradVertical', { label: 'Peso alto→basso', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uForceGradVertical.value = e.value })
    gradFolder.addBinding(params, 'forceGradHorizontal', { label: 'Peso centro→bordi', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { compute.velocityVariable.material.uniforms.uForceGradHorizontal.value = e.value })

    // --- Camera ---
    const cameraFolder = pane.addFolder({ title: 'Camera', expanded: folderExpanded('cameraFolder') })
    cameraFolder.addBinding(params, 'enableRotate', { label: 'Rotazione' })
        .on('change', (e) => { controls.enableRotate = e.value })
    cameraFolder.addBinding(params, 'enableZoom', { label: 'Zoom' })
        .on('change', (e) => { controls.enableZoom = e.value })
    cameraFolder.addBinding(params, 'enablePan', { label: 'Pan' })
        .on('change', (e) => { controls.enablePan = e.value })
    cameraFolder.addBinding(params, 'autoRotate', { label: 'Rotazione automatica' })
        .on('change', (e) => { controls.autoRotate = e.value })
    cameraFolder.addBinding(params, 'autoRotateSpeed', { label: 'Velocità rotazione', min: 0, max: 10, step: 0.1 })
        .on('change', (e) => { controls.autoRotateSpeed = e.value })
    // Dolly sull'asse z: allontana/avvicina la camera per ispezionare la linea
    // (equivalente ai tasti − / = / 0 nell'overlay carbon)
    const camDolly = { z: controls.object.position.z }
    cameraFolder.addBinding(camDolly, 'z', { label: 'Distanza Z (dolly)', min: 4, max: 200, step: 1 })
        .on('change', (e) => { controls.object.position.z = e.value; controls.update() })

    // --- Bloom ---
    const bloomFolder = pane.addFolder({ title: 'Bloom', expanded: folderExpanded('bloomFolder') })
    bloomFolder.addBinding(params, 'bloomEnabled', { label: 'Attivo' })
    bloomFolder.addBinding(params, 'bloomStrength', { label: 'Intensità', min: 0, max: 10, step: 0.01 })
        .on('change', (e) => { bloomPass.strength = e.value })
    bloomFolder.addBinding(params, 'bloomRadius', { label: 'Raggio', min: 0, max: 5, step: 0.01 })
        .on('change', (e) => { bloomPass.radius = e.value })
    bloomFolder.addBinding(params, 'bloomThreshold', { label: 'Soglia', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { bloomPass.threshold = e.value })

    // --- HDR ---
    const hdrFolder = pane.addFolder({ title: 'HDR / Tone Mapping', expanded: folderExpanded('hdrFolder') })
    hdrFolder.addBinding(params, 'toneMapping', {
        label: 'Tone Mapping',
        options: { 'Nessuno': 0, 'Lineare': 1, 'Reinhard': 2, 'Cineon': 3, 'ACES Filmic': 4, 'AgX': 5, 'Neutral': 6 }
    }).on('change', (e) => { renderer.toneMapping = e.value })
    hdrFolder.addBinding(params, 'exposure', { label: 'Esposizione', min: 0.1, max: 10, step: 0.01 })
        .on('change', (e) => { renderer.toneMappingExposure = e.value })

    // --- Profondità di campo ---
    const dofFolder = pane.addFolder({ title: 'Profondità di campo', expanded: folderExpanded('dofFolder') })
    const updateDofUniforms = () => {
        const u = particleSystem.material.uniforms
        if (params.dofEnabled) {
            u.uDofFocus.value = params.dofFocus
            u.uDofAperture.value = params.dofAperture
            u.uDofMaxBlur.value = params.dofMaxBlur
        } else {
            u.uDofFocus.value = 0.0
            u.uDofAperture.value = 0.0
            u.uDofMaxBlur.value = 0.0
        }
    }
    dofFolder.addBinding(params, 'dofEnabled', { label: 'Attivo' }).on('change', updateDofUniforms)
    dofFolder.addBinding(params, 'dofFocus', { label: 'Fuoco', min: 0.1, max: 80, step: 0.1 }).on('change', updateDofUniforms)
    dofFolder.addBinding(params, 'dofAperture', { label: 'Apertura', min: 0.001, max: 1, step: 0.001 }).on('change', updateDofUniforms)
    dofFolder.addBinding(params, 'dofMaxBlur', { label: 'Blur max', min: 0, max: 5, step: 0.01 }).on('change', updateDofUniforms)

    // --- Trail ---
    const trailFolder = pane.addFolder({ title: 'Trail (Persistenza)', expanded: folderExpanded('trailFolder') })
    trailFolder.addBinding(params, 'trailEnabled', { label: 'Attivo' })
    trailFolder.addBinding(params, 'trailBlendMode', { label: 'Blend mode', options: { 'Max (glow)': 0, 'Screen (additivo)': 1, 'Lineare (classico)': 2 } })
        .on('change', (e) => { trailFeedbackMaterial.uniforms.blendMode.value = e.value; trailOutputMaterial.uniforms.blendMode.value = e.value })
    trailFolder.addBinding(params, 'trailAmount', { label: 'Persistenza', min: 0, max: 99.9, step: 0.1 })
        .on('change', (e) => { trailFeedbackMaterial.uniforms.trailAmount.value = e.value / 100; trailOutputMaterial.uniforms.trailAmount.value = e.value / 100 })
    trailFolder.addBinding(params, 'trailBrightPersist', { label: 'Persist. luminosità', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { trailFeedbackMaterial.uniforms.brightPersist.value = e.value; trailOutputMaterial.uniforms.brightPersist.value = e.value })
    trailFolder.addBinding(params, 'trailBlurAmount', { label: 'Diffusione', min: 0, max: 1, step: 0.01 })
        .on('change', (e) => { trailFeedbackMaterial.uniforms.blurAmount.value = e.value })
    trailFolder.addBinding(params, 'trailResolutionScale', { label: 'Scala risoluzione', min: 0.1, max: 1, step: 0.05 })
        .on('change', (e) => {
            const tw = Math.floor(viewport.width * viewport.pixelRatio * e.value)
            const th = Math.floor(viewport.height * viewport.pixelRatio * e.value)
            trailRenderTargetA.setSize(tw, th)
            trailRenderTargetB.setSize(tw, th)
            trailFeedbackMaterial.uniforms.texelSize.value.set(1.0 / tw, 1.0 / th)
        })

    // --- Azioni ---
    const actionsFolder = pane.addFolder({ title: 'Azioni', expanded: folderExpanded('actionsFolder') })
    actionsFolder.addButton({ title: 'Esporta configurazione' }).on('click', () => {
        const json = JSON.stringify(params, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'particles-config.json'
        a.click()
        URL.revokeObjectURL(url)
    })

    actionsFolder.addButton({ title: 'Ripristina default' }).on('click', () => {
        if (!confirm('Ripristinare tutti i parametri ai valori predefiniti?')) return
        Object.assign(params, defaultParams)
        localStorage.removeItem(storageKey)
        localStorage.removeItem(FOLDERS_KEY)
        applyParams()
    })

    pane.on('change', () => { saveParams() })

    // --- Folder state persistence ---
    const allFolders = { presetFolder, particlesFolder, morphFolder, aspectFolder, flowFolder, mouseFolder, suctionFolder, physicsFolder, cameraFolder, bloomFolder, hdrFolder, dofFolder, trailFolder, actionsFolder }
    const persistFolderState = () => {
        const state = {}
        for (const [key, folder] of Object.entries(allFolders)) {
            state[key] = folder.expanded
        }
        saveFolderState(state)
    }
    for (const folder of Object.values(allFolders)) {
        folder.on('fold', () => { persistFolderState() })
    }

    const fpsGraph = pane.addBlade({ view: 'fpsgraph', label: 'FPS', rows: 2 })

    // --- Drag handle + persisted position ---
    const paneWrapper = pane.element.parentElement
    if (paneWrapper) {
        const handle = document.createElement('div')
        handle.className = 'tp-drag-handle'
        handle.textContent = 'Trascina'
        paneWrapper.insertBefore(handle, pane.element)

        const POS_KEY = 'falmec-tp-pos'
        try {
            const saved = JSON.parse(localStorage.getItem(POS_KEY))
            if (saved && typeof saved.left === 'number' && typeof saved.top === 'number') {
                paneWrapper.style.right = 'auto'
                paneWrapper.style.bottom = 'auto'
                paneWrapper.style.left = saved.left + 'px'
                paneWrapper.style.top = saved.top + 'px'
            }
        } catch(e) {}

        let pressed = false
        let dragging = false
        let downX = 0, downY = 0, baseX = 0, baseY = 0

        handle.addEventListener('pointerdown', (e) => {
            pressed = true
            dragging = false
            downX = e.clientX
            downY = e.clientY
            const rect = paneWrapper.getBoundingClientRect()
            baseX = rect.left
            baseY = rect.top
            handle.setPointerCapture(e.pointerId)
            e.preventDefault()
        })
        handle.addEventListener('pointermove', (e) => {
            if (!pressed) return
            const dx = e.clientX - downX
            const dy = e.clientY - downY
            if (!dragging && Math.abs(dx) + Math.abs(dy) > 3) {
                dragging = true
                paneWrapper.style.right = 'auto'
                paneWrapper.style.bottom = 'auto'
            }
            if (dragging) {
                const nx = Math.max(0, Math.min(window.innerWidth - 40, baseX + dx))
                const ny = Math.max(0, Math.min(window.innerHeight - 40, baseY + dy))
                paneWrapper.style.left = nx + 'px'
                paneWrapper.style.top = ny + 'px'
            }
        })
        handle.addEventListener('pointerup', (e) => {
            if (dragging) {
                const rect = paneWrapper.getBoundingClientRect()
                try { localStorage.setItem(POS_KEY, JSON.stringify({ left: rect.left, top: rect.top })) } catch(e) {}
            }
            pressed = false
            dragging = false
            handle.releasePointerCapture(e.pointerId)
        })
    }

    return { pane, fpsGraph }
}
